import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { LdapManagedUser, LdapGroupInfo } from 'shared';
import { ldapUsersApi, ldapErrorMessage } from '../../api/ldapUsers';
import { useAuth } from '../../contexts/AuthContext';
import { LdapUserWizard } from './LdapUserWizard';
import { EditLdapUserDialog } from './EditLdapUserDialog';

type StatusFilter = 'all' | 'active' | 'archived';

export const LdapUserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const toast = useRef<Toast>(null);

  const [users, setUsers] = useState<LdapManagedUser[]>([]);
  const [groups, setGroups] = useState<LdapGroupInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [wizardVisible, setWizardVisible] = useState(false);
  const [wizardTemplate, setWizardTemplate] = useState<LdapManagedUser | null>(null);
  const [editUser, setEditUser] = useState<LdapManagedUser | null>(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const [userList, groupList] = await Promise.all([
        ldapUsersApi.list(),
        ldapUsersApi.listGroups(),
      ]);
      setUsers(userList);
      setGroups(groupList);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: ldapErrorMessage(err, 'Failed to load LDAP users'),
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const filteredUsers = users.filter((u) => {
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    if (search) {
      const term = search.toLowerCase();
      return (
        u.uid.toLowerCase().includes(term) ||
        u.cn.toLowerCase().includes(term) ||
        u.mail.toLowerCase().includes(term) ||
        u.groups.some((g) => g.toLowerCase().includes(term))
      );
    }
    return true;
  });

  const openWizard = (template: LdapManagedUser | null) => {
    setWizardTemplate(template);
    setWizardVisible(true);
  };

  const handleArchive = (user: LdapManagedUser) => {
    confirmDialog({
      message: (
        <div>
          <p>
            Archive <strong>{user.uid}</strong> ({user.cn})?
          </p>
          <p className="mt-2 text-sm">
            The user will be removed from{' '}
            {user.groups.length > 0 ? (
              <>
                the groups <strong>{user.groups.join(', ')}</strong>
              </>
            ) : (
              'all groups'
            )}{' '}
            and can no longer log in to any LDAP-backed service (latte website, Gitea,
            MemberHub). This can be undone with Unarchive.
          </p>
        </div>
      ),
      header: 'Archive LDAP User',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Archive',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await ldapUsersApi.archive(user.uid);
          toast.current?.show({
            severity: 'success',
            summary: 'Archived',
            detail: `${user.uid} was archived`,
            life: 3000,
          });
          reload();
        } catch (err: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: ldapErrorMessage(err, 'Failed to archive user'),
            life: 5000,
          });
        }
      },
    });
  };

  const handleUnarchive = (user: LdapManagedUser) => {
    const restored = user.archivedGroups || [];
    confirmDialog({
      message: (
        <div>
          <p>
            Restore <strong>{user.uid}</strong> ({user.cn})?
          </p>
          <p className="mt-2 text-sm">
            {restored.length > 0 ? (
              <>
                Group memberships to restore: <strong>{restored.join(', ')}</strong>
              </>
            ) : (
              'No recorded group memberships to restore - assign groups via Edit afterwards.'
            )}
          </p>
        </div>
      ),
      header: 'Unarchive LDAP User',
      icon: 'pi pi-replay',
      acceptLabel: 'Unarchive',
      acceptClassName: 'p-button-success',
      accept: async () => {
        try {
          const result = await ldapUsersApi.unarchive(user.uid);
          if (result.skippedGroups?.length) {
            toast.current?.show({
              severity: 'warn',
              summary: 'Restored with warnings',
              detail: `Groups no longer exist and were skipped: ${result.skippedGroups.join(', ')}`,
              life: 6000,
            });
          } else {
            toast.current?.show({
              severity: 'success',
              summary: 'Restored',
              detail: `${user.uid} was unarchived`,
              life: 3000,
            });
          }
          reload();
        } catch (err: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: ldapErrorMessage(err, 'Failed to unarchive user'),
            life: 5000,
          });
        }
      },
    });
  };

  const groupsBodyTemplate = (user: LdapManagedUser) => {
    const shown = user.status === 'archived' ? user.archivedGroups || [] : user.groups;
    const org = shown.filter((g) => groups.find((gi) => gi.cn === g)?.isOrgGroup);
    const roles = shown.filter((g) => !groups.find((gi) => gi.cn === g)?.isOrgGroup);
    const visibleRoles = roles.slice(0, 3);
    const overflow = roles.length - visibleRoles.length;
    return (
      <div className="flex flex-wrap gap-1 items-center">
        {org.map((g) => (
          <Tag key={g} value={g} severity="info" />
        ))}
        {visibleRoles.map((g) => (
          <Tag key={g} value={g} className="bg-transparent border border-gray-300 text-gray-600" />
        ))}
        {overflow > 0 && (
          <span className="text-xs text-gray-500" title={roles.slice(3).join(', ')}>
            +{overflow}
          </span>
        )}
        {user.status === 'archived' && shown.length > 0 && (
          <span className="text-xs text-gray-400 italic ml-1">(restored on unarchive)</span>
        )}
      </div>
    );
  };

  const statusBodyTemplate = (user: LdapManagedUser) => (
    <Tag
      value={user.status === 'active' ? 'Active' : 'Archived'}
      severity={user.status === 'active' ? 'success' : 'warning'}
    />
  );

  const actionsBodyTemplate = (user: LdapManagedUser) => {
    const isSelf = currentUser?.username === user.uid;
    if (user.status === 'archived') {
      return (
        <Button
          icon="pi pi-replay"
          className="p-button-rounded p-button-text p-button-success"
          tooltip="Unarchive"
          tooltipOptions={{ position: 'left' }}
          onClick={() => handleUnarchive(user)}
        />
      );
    }
    return (
      <div className="flex gap-1 justify-end">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          onClick={() => setEditUser(user)}
        />
        <Button
          icon="pi pi-copy"
          className="p-button-rounded p-button-text p-button-help"
          tooltip="Create new user from this one"
          tooltipOptions={{ position: 'top' }}
          onClick={() => openWizard(user)}
        />
        <Button
          icon="pi pi-inbox"
          className="p-button-rounded p-button-text p-button-danger"
          tooltip={isSelf ? 'You cannot archive your own account' : 'Archive'}
          tooltipOptions={{ position: 'left', showOnDisabled: true }}
          disabled={isSelf}
          onClick={() => handleArchive(user)}
        />
      </div>
    );
  };

  const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Archived', value: 'archived' },
  ];

  return (
    <div data-component="ldap-user-management">
      <Toast ref={toast} />
      <ConfirmDialog data-component="ldap-confirm-dialog" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold">LDAP User Management</h2>
          <p className="text-sm text-gray-500">
            Directory accounts for the latte website, Gitea, and MemberHub
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full"
            />
          </span>
          <Dropdown
            value={statusFilter}
            options={statusOptions}
            onChange={(e) => setStatusFilter(e.value)}
            className="w-full sm:w-36"
          />
          <Button
            label="New User"
            icon="pi pi-user-plus"
            onClick={() => openWizard(null)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col gap-2" data-component="ldap-user-skeleton">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height="3rem" />
          ))}
        </div>
      ) : (
        <DataTable
          value={filteredUsers}
          paginator
          rows={20}
          rowsPerPageOptions={[10, 20, 50]}
          className="p-datatable-sm"
          showGridlines
          stripedRows
          scrollable
          emptyMessage="No LDAP users found"
          sortField="uid"
          sortOrder={1}
        >
          <Column field="uid" header="Username" sortable style={{ minWidth: '10rem' }} />
          <Column field="cn" header="Name" sortable style={{ minWidth: '12rem' }} />
          <Column field="mail" header="Email" sortable style={{ minWidth: '14rem' }} />
          <Column header="Groups" body={groupsBodyTemplate} style={{ minWidth: '16rem' }} />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            sortable
            style={{ width: '8rem' }}
          />
          <Column
            header="Actions"
            body={actionsBodyTemplate}
            style={{ width: '10rem' }}
            frozen
            alignFrozen="right"
          />
        </DataTable>
      )}

      <LdapUserWizard
        visible={wizardVisible}
        onHide={() => {
          setWizardVisible(false);
          setWizardTemplate(null);
        }}
        onCreated={() => reload()}
        groups={groups}
        users={users}
        template={wizardTemplate}
      />

      <EditLdapUserDialog
        user={editUser}
        groups={groups}
        onHide={() => setEditUser(null)}
        onSaved={() => {
          setEditUser(null);
          reload();
        }}
      />
    </div>
  );
};
